import {
  ChatIcon,
  CodeIcon,
  CubeIcon,
  GlobeAltIcon,
  SearchIcon,
  SparklesIcon,
} from "@heroicons/react/outline";
import {
  ArrowUpDownIcon,
  CalculatorIcon,
  CheckCircleIcon,
  CompassIcon,
  CurlyBraces,
  FileOutputIcon,
  FileTextIcon,
  GitMergeIcon,
  HashIcon,
  LinkIcon,
  ListTree,
  MergeIcon,
  MessagesSquare,
  RefreshCwIcon,
  RegexIcon,
  RulerIcon,
  RouteIcon,
  ScaleIcon,
  ScissorsIcon,
  ServerCog,
  UserIcon,
} from "lucide-react";
import { ReactNode } from "react";

import { BlockProps } from "../Block";

import {
  DataSourceBlocks,
  HelperFunctionBlocks,
  LLMEvalBlocks,
  SimpleEvalBlocks,
} from "./categories";

import { CopyButton } from "@/components/ui/copy-button";
import { Agent } from "@/components/ui/icons";

export enum BlockCategory {
  DATA_SOURCES = "Data Sources",
  SIMPLE_EVALS = "Simple Evals",
  LLM_EVALS = "LLM Evals",
  HELPER_FUNCTIONS = "Helper Functions",
}

export const BlockCategoryList = Object.values(BlockCategory);

export enum RawBlockType {
  DATASET = "DATASET",
  PROMPT_TEMPLATE = "PROMPT_TEMPLATE",
  CONVERSATION_SIMULATOR = "CONVERSATION_SIMULATOR",
  ENDPOINT = "ENDPOINT",
  MCP = "MCP",
  HUMAN = "HUMAN",
  COMPARE = "COMPARE",
  COALESCE = "COALESCE",
  CONDITION = "CONDITION",
  ABSOLUTE_NUMERIC_DISTANCE = "ABSOLUTE_NUMERIC_DISTANCE",
  REGEX = "REGEX",
  JSON_PATH = "JSON_PATH",
  PARSE_VALUE = "PARSE_VALUE",
  CONTAINS = "CONTAINS",
  LLM_ASSERTION = "LLM_ASSERTION",
  AI_DATA_EXTRACTION = "AI_DATA_EXTRACTION",
  VARIABLE = "VARIABLE",
  ASSERT_VALID = "ASSERT_VALID",
  COSINE_SIMILARITY = "COSINE_SIMILARITY",
  COUNT = "COUNT",
  MATH_OPERATOR = "MATH_OPERATOR",
  XML_PATH = "XML_PATH",
  REGEX_EXTRACTION = "REGEX_EXTRACTION",
  COMBINE_COLUMNS = "COMBINE_COLUMNS",
  MIN_MAX = "MIN_MAX",
  CODE_EXECUTION = "CODE_EXECUTION",
  WORKFLOW = "WORKFLOW",
  WHILE_LOOP = "WHILE_LOOP",
  FOR_LOOP = "FOR_LOOP",
  APPLY_DIFF = "APPLY_DIFF",
  TRAJECTORY = "TRAJECTORY",
  // Must match backend ColumnType.COMPOSITION ("COMPOSITION"). The raw
  // string is the on-the-wire `type` field sent to
  // POST/PATCH `.../columns`.
  COMPOSITION = "COMPOSITION",
}

// BlockType should use same keys as RawBlockType
export enum BlockType {
  PROMPT_TEMPLATE = "Prompt Template",
  CONVERSATION_SIMULATOR = "Conversation Simulator",
  ENDPOINT = "Endpoint",
  MCP = "MCP",
  HUMAN = "Human",
  COMPARE = "Compare",
  COALESCE = "Coalesce",
  CONDITION = "Condition",
  ABSOLUTE_NUMERIC_DISTANCE = "Absolute Numeric Distance",
  REGEX = "Regex",
  JSON_PATH = "JSON Extraction",
  PARSE_VALUE = "Parse Value",
  CONTAINS = "Contains",
  LLM_ASSERTION = "AI Judge",
  AI_DATA_EXTRACTION = "AI Data Extraction",
  VARIABLE = "Static",
  ASSERT_VALID = "Assert Valid",
  COSINE_SIMILARITY = "Cosine Similarity",
  COUNT = "Count",
  MATH_OPERATOR = "Math Operator",
  XML_PATH = "XML Path",
  REGEX_EXTRACTION = "Regex Extraction",
  COMBINE_COLUMNS = "Combine Columns",
  MIN_MAX = "Min Max",
  CODE_EXECUTION = "Code Execution",
  WORKFLOW = "Workflow",
  WHILE_LOOP = "While Loop",
  FOR_LOOP = "For Loop",
  APPLY_DIFF = "Apply Diff",
  TRAJECTORY = "Trajectory",
  COMPOSITION = "Composition",
}

// Convert BlockType to RawBlockType
export function blockTypeToRawBlockType(blockType: BlockType): RawBlockType {
  const blockTypeToRawBlockTypeMap: Record<BlockType, RawBlockType> = {
    [BlockType.PROMPT_TEMPLATE]: RawBlockType.PROMPT_TEMPLATE,
    [BlockType.CONVERSATION_SIMULATOR]: RawBlockType.CONVERSATION_SIMULATOR,
    [BlockType.ENDPOINT]: RawBlockType.ENDPOINT,
    [BlockType.MCP]: RawBlockType.MCP,
    [BlockType.HUMAN]: RawBlockType.HUMAN,
    [BlockType.COMPARE]: RawBlockType.COMPARE,
    [BlockType.COALESCE]: RawBlockType.COALESCE,
    [BlockType.CONDITION]: RawBlockType.CONDITION,
    [BlockType.ABSOLUTE_NUMERIC_DISTANCE]: RawBlockType.ABSOLUTE_NUMERIC_DISTANCE,
    [BlockType.REGEX]: RawBlockType.REGEX,
    [BlockType.JSON_PATH]: RawBlockType.JSON_PATH,
    [BlockType.PARSE_VALUE]: RawBlockType.PARSE_VALUE,
    [BlockType.CONTAINS]: RawBlockType.CONTAINS,
    [BlockType.LLM_ASSERTION]: RawBlockType.LLM_ASSERTION,
    [BlockType.AI_DATA_EXTRACTION]: RawBlockType.AI_DATA_EXTRACTION,
    [BlockType.VARIABLE]: RawBlockType.VARIABLE,
    [BlockType.ASSERT_VALID]: RawBlockType.ASSERT_VALID,
    [BlockType.COSINE_SIMILARITY]: RawBlockType.COSINE_SIMILARITY,
    [BlockType.COUNT]: RawBlockType.COUNT,
    [BlockType.MATH_OPERATOR]: RawBlockType.MATH_OPERATOR,
    [BlockType.XML_PATH]: RawBlockType.XML_PATH,
    [BlockType.REGEX_EXTRACTION]: RawBlockType.REGEX_EXTRACTION,
    [BlockType.COMBINE_COLUMNS]: RawBlockType.COMBINE_COLUMNS,
    [BlockType.MIN_MAX]: RawBlockType.MIN_MAX,
    [BlockType.CODE_EXECUTION]: RawBlockType.CODE_EXECUTION,
    [BlockType.WORKFLOW]: RawBlockType.WORKFLOW,
    [BlockType.WHILE_LOOP]: RawBlockType.WHILE_LOOP,
    [BlockType.FOR_LOOP]: RawBlockType.FOR_LOOP,
    [BlockType.APPLY_DIFF]: RawBlockType.APPLY_DIFF,
    [BlockType.TRAJECTORY]: RawBlockType.TRAJECTORY,
    [BlockType.COMPOSITION]: RawBlockType.COMPOSITION,
  };

  return blockTypeToRawBlockTypeMap[blockType];
}

export function rawBlockTypeToBlockType(rawBlockType: RawBlockType): BlockType {
  const rawBlockTypeToBlockTypeMap: Record<RawBlockType, BlockType> = {
    [RawBlockType.PROMPT_TEMPLATE]: BlockType.PROMPT_TEMPLATE,
    [RawBlockType.CONVERSATION_SIMULATOR]: BlockType.CONVERSATION_SIMULATOR,
    [RawBlockType.ENDPOINT]: BlockType.ENDPOINT,
    [RawBlockType.MCP]: BlockType.MCP,
    [RawBlockType.HUMAN]: BlockType.HUMAN,
    [RawBlockType.COMPARE]: BlockType.COMPARE,
    [RawBlockType.COALESCE]: BlockType.COALESCE,
    [RawBlockType.CONDITION]: BlockType.CONDITION,
    [RawBlockType.ABSOLUTE_NUMERIC_DISTANCE]: BlockType.ABSOLUTE_NUMERIC_DISTANCE,
    [RawBlockType.REGEX]: BlockType.REGEX,
    [RawBlockType.JSON_PATH]: BlockType.JSON_PATH,
    [RawBlockType.PARSE_VALUE]: BlockType.PARSE_VALUE,
    [RawBlockType.CONTAINS]: BlockType.CONTAINS,
    [RawBlockType.LLM_ASSERTION]: BlockType.LLM_ASSERTION,
    [RawBlockType.AI_DATA_EXTRACTION]: BlockType.AI_DATA_EXTRACTION,
    [RawBlockType.VARIABLE]: BlockType.VARIABLE,
    [RawBlockType.ASSERT_VALID]: BlockType.ASSERT_VALID,
    [RawBlockType.COSINE_SIMILARITY]: BlockType.COSINE_SIMILARITY,
    [RawBlockType.COUNT]: BlockType.COUNT,
    [RawBlockType.MATH_OPERATOR]: BlockType.MATH_OPERATOR,
    [RawBlockType.XML_PATH]: BlockType.XML_PATH,
    [RawBlockType.REGEX_EXTRACTION]: BlockType.REGEX_EXTRACTION,
    [RawBlockType.COMBINE_COLUMNS]: BlockType.COMBINE_COLUMNS,
    [RawBlockType.MIN_MAX]: BlockType.MIN_MAX,
    [RawBlockType.CODE_EXECUTION]: BlockType.CODE_EXECUTION,
    [RawBlockType.WORKFLOW]: BlockType.WORKFLOW,
    [RawBlockType.WHILE_LOOP]: BlockType.WHILE_LOOP,
    [RawBlockType.FOR_LOOP]: BlockType.FOR_LOOP,
    [RawBlockType.DATASET]: BlockType.VARIABLE, // Map DATASET to VARIABLE as fallback
    [RawBlockType.APPLY_DIFF]: BlockType.APPLY_DIFF,
    [RawBlockType.TRAJECTORY]: BlockType.TRAJECTORY,
    [RawBlockType.COMPOSITION]: BlockType.COMPOSITION,
  };

  return rawBlockTypeToBlockTypeMap[rawBlockType];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface BlockDefinition<TConfiguration = any> {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  short_description: string;
  long_description: string | JSX.Element;
  render: (props: BlockProps<TConfiguration>) => JSX.Element | ReactNode;
  category?: BlockCategory;
  weight?: number;
}

// Function to automatically derive category from the render function
const getCategory = (
  renderFn: (props: BlockProps<unknown>) => JSX.Element | ReactNode,
): BlockCategory => {
  const imports = {
    [BlockCategory.DATA_SOURCES]: DataSourceBlocks,
    [BlockCategory.SIMPLE_EVALS]: SimpleEvalBlocks,
    [BlockCategory.LLM_EVALS]: LLMEvalBlocks,
    [BlockCategory.HELPER_FUNCTIONS]: HelperFunctionBlocks,
  };

  for (const [category, namespace] of Object.entries(imports)) {
    if (Object.values(namespace).includes(renderFn)) {
      return category as BlockCategory;
    }
  }
  return BlockCategory.HELPER_FUNCTIONS;
};

// Transform function to add categories
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withCategories = <T extends Record<string, any>>(blocks: T): T => {
  const result = {} as T;
  for (const [key, block] of Object.entries(blocks)) {
    result[key as keyof T] = {
      ...block,
      category: getCategory(block.render),
    };
  }
  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const blocksDefinition: Record<BlockType, BlockDefinition<any>> = {
  // Data Sources
  [BlockType.PROMPT_TEMPLATE]: {
    icon: ChatIcon,
    short_description: "Run a prompt through an LLM",
    long_description:
      "Create and manage prompt templates with variables and formatting for consistent AI interactions",
    render: DataSourceBlocks.PromptTemplateBlock,
    weight: 1,
  },
  [BlockType.WORKFLOW]: {
    icon: Agent,
    short_description: "Execute a nested workflow",
    long_description: "Execute nested workflows within the current workflow",
    render: DataSourceBlocks.WorkflowBlock,
    weight: 2,
  },
  [BlockType.CODE_EXECUTION]: {
    icon: CodeIcon,
    short_description: "Run code for each row",
    long_description:
      "Execute custom code in various programming languages with input/output handling",
    render: DataSourceBlocks.CodeBlock,
    weight: 3,
  },
  [BlockType.ENDPOINT]: {
    icon: GlobeAltIcon,
    short_description: "Send data to your URL endpoint",
    long_description:
      "Make HTTP POST requests to external APIs and services with configurable parameters",
    render: DataSourceBlocks.EndpointBlock,
    weight: 4,
  },
  [BlockType.MCP]: {
    icon: ServerCog,
    short_description: "Perform MCP functions",
    long_description: "Connect to Model Context Protocol (MCP) servers for extended functionality",
    render: DataSourceBlocks.MCPBlock,
    weight: 5,
  },
  [BlockType.CONVERSATION_SIMULATOR]: {
    icon: MessagesSquare,
    short_description: "Simulate a user and assistant conversation",
    long_description:
      "Simulate multi-turn conversations between different participants for testing and evaluation",
    render: DataSourceBlocks.ConversationSimulatorBlock,
    weight: 6,
  },
  [BlockType.VARIABLE]: {
    icon: CubeIcon,
    short_description: "Provide a static value as input",
    long_description: "Static value available throughout (e.g., constants, config).",
    render: DataSourceBlocks.StaticValueBlock,
    weight: 7,
  },
  [BlockType.HUMAN]: {
    icon: UserIcon,
    short_description: "Let a human fill in the data",
    long_description: "Pause workflow execution to request input or approval from a human user",
    render: DataSourceBlocks.HumanInputBlock,
    weight: 8,
  },
  [BlockType.WHILE_LOOP]: {
    icon: RefreshCwIcon,
    short_description: "Execute prompts or workflows repeatedly while a condition is true",
    long_description:
      "Execute a prompt template or workflow repeatedly while a condition is true. The loop continues until the end condition evaluates to false.",
    render: DataSourceBlocks.LoopBlock,
    weight: 9,
  },
  [BlockType.FOR_LOOP]: {
    icon: RefreshCwIcon,
    short_description: "Iterate over a collection or array",
    long_description:
      "Execute a prompt template or workflow for each item in a collection or array. The loop iterates over all items in the specified collection.",
    render: DataSourceBlocks.ForLoopBlock,
    weight: 10,
  },
  [BlockType.COMPOSITION]: {
    icon: LinkIcon,
    short_description: "Reference data from another sheet or table",
    long_description:
      "Bind this column to a sheet from the current table or another table in the workspace. Each row pulls a value from the chosen sheet.",
    render: DataSourceBlocks.CompositionBlock,
    weight: 11,
  },

  // Simple Evals
  [BlockType.COMPARE]: {
    icon: ScaleIcon,
    short_description: "Generate a diff between two values",
    long_description: "Compare two values using various comparison methods and return the result",
    render: SimpleEvalBlocks.CompareBlock,
    weight: 1,
  },
  [BlockType.CONDITION]: {
    icon: CheckCircleIcon,
    short_description: "Branch execution based on condition checks",
    long_description:
      "Evaluate one or more conditions and continue workflow execution only when they pass",
    render: SimpleEvalBlocks.ConditionBlock,
    weight: 2,
  },
  [BlockType.MATH_OPERATOR]: {
    icon: CalculatorIcon,
    short_description: "Apply mathematical comparison operators",
    long_description: "Apply mathematical comparison operators (>, <, =, >=, <=, !=) to values",
    render: SimpleEvalBlocks.ComparisonOperatorBlock,
    weight: 3,
  },
  [BlockType.CONTAINS]: {
    icon: SearchIcon,
    short_description: "Check if text contains a substring",
    long_description: "Check if a text string contains a specific substring or pattern",
    render: SimpleEvalBlocks.ContainsBlock,
    weight: 4,
  },
  [BlockType.REGEX]: {
    icon: RegexIcon,
    short_description: "Match text patterns with regular expressions",
    long_description: "Match text patterns using regular expressions for validation and extraction",
    render: SimpleEvalBlocks.RegexBlock,
    weight: 5,
  },
  [BlockType.ABSOLUTE_NUMERIC_DISTANCE]: {
    icon: RulerIcon,
    short_description: "Measure distance between numeric values",
    long_description:
      "Calculate the absolute difference between two numeric values for comparison and evaluation",
    render: SimpleEvalBlocks.AbsoluteNumericDistanceBlock,
    weight: 6,
  },
  [BlockType.ASSERT_VALID]: {
    icon: CheckCircleIcon,
    short_description: "Validate data types and formats",
    long_description: "Validate that input data matches expected types and formats",
    render: SimpleEvalBlocks.TypeValidationBlock,
    weight: 7,
  },
  [BlockType.TRAJECTORY]: {
    icon: RouteIcon,
    short_description: "Match agent tool call trajectories",
    long_description:
      "Check that an agent trace follows an expected tool sequence from a tool list or imported column",
    render: SimpleEvalBlocks.TrajectoryBlock,
    weight: 8,
  },

  // LLM Evals
  [BlockType.LLM_ASSERTION]: {
    icon: SparklesIcon,
    short_description: "Make AI-powered assertions and validations",
    long_description:
      "Use AI to make assertions and validate conditions with natural language reasoning",
    render: LLMEvalBlocks.LLMAssertionBlock,
    weight: 1,
  },
  [BlockType.AI_DATA_EXTRACTION]: {
    icon: FileOutputIcon,
    short_description: "Extract information using AI",
    long_description:
      "Use AI to extract specific information from text based on natural language queries",
    render: LLMEvalBlocks.AIDataExtractionBlock,
    weight: 2,
  },
  [BlockType.COSINE_SIMILARITY]: {
    icon: CompassIcon,
    short_description: "Calculate similarity between vectors",
    long_description: "Calculate the cosine similarity between two vectors or text embeddings",
    render: LLMEvalBlocks.CosineSimilarityBlock,
    weight: 3,
  },

  // Helper Functions
  [BlockType.JSON_PATH]: {
    icon: CurlyBraces,
    short_description: "Extract data from JSON documents",
    long_description: "Extract and parse data from JSON documents using JSONPath expressions",
    render: HelperFunctionBlocks.JSONExtractionBlock,
    weight: 1,
  },
  [BlockType.XML_PATH]: {
    icon: ListTree,
    short_description: "Extract data from XML documents",
    long_description: "Extract and parse data from XML documents using XPath expressions",
    render: HelperFunctionBlocks.XMLExtractionBlock,
    weight: 2,
  },
  [BlockType.REGEX_EXTRACTION]: {
    icon: ScissorsIcon,
    short_description: "Extract text with regular expressions",
    long_description: "Extract specific parts of text using regular expression capture groups",
    render: HelperFunctionBlocks.RegexExtractionBlock,
    weight: 3,
  },
  [BlockType.PARSE_VALUE]: {
    icon: FileTextIcon,
    short_description: "Parse specific values from data",
    long_description: "Parse and extract specific values from structured or unstructured data",
    render: HelperFunctionBlocks.ParseValueBlock,
    weight: 4,
  },
  [BlockType.COUNT]: {
    icon: HashIcon,
    short_description: "Count elements or occurrences",
    long_description: "Count the number of elements, characters, or occurrences in a given input",
    render: HelperFunctionBlocks.CountBlock,
    weight: 6,
  },
  [BlockType.MIN_MAX]: {
    icon: ArrowUpDownIcon,
    short_description: "Find minimum or maximum values",
    long_description: "Find the minimum or maximum value from a set of numeric inputs",
    render: HelperFunctionBlocks.MinMaxBlock,
    weight: 7,
  },
  [BlockType.COALESCE]: {
    icon: MergeIcon,
    short_description: "Return the first non-null value",
    long_description: "Return the first non-null, non-undefined value from a list of inputs",
    render: HelperFunctionBlocks.CoalesceBlock,
    weight: 8,
  },
  [BlockType.COMBINE_COLUMNS]: {
    icon: LinkIcon,
    short_description: "Merge results from multiple sources",
    long_description:
      "Merge and combine results from multiple sources into a single structured output",
    render: HelperFunctionBlocks.CombineResultsBlock,
    weight: 9,
  },
  [BlockType.APPLY_DIFF]: {
    icon: GitMergeIcon,
    short_description: "Apply diff patches to text content",
    long_description: (
      <div className="space-y-3">
        <p>
          Apply diff patches to text. Use for applying code changes, editing documents, or just
          saving tokens on edits. Requires standard unified diff format with file headers.
        </p>
        <p className="text-sm text-gray-600">
          See{" "}
          <a
            href="https://pypi.org/project/unidiff/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
            onClick={e => e.stopPropagation()}
          >
            unidiff
          </a>{" "}
          for details.
        </p>
        <CopyButton
          labeledButton
          buttonLabel="Copy instructions for LLMs"
          text={`## Unified Diff Specification (strict unidiff)

Produce a valid **unified diff** with file headers and hunk headers. Only modifications of existing text are supported (no file creation or deletion).

### File headers (required)
- Old (source):  \`--- a/<filename>\`
- New (target):  \`+++ b/<filename>\`
- Use consistent prefixes \`a/\` and \`b/\`.

### Hunk headers (required for every changed region)
- Format: \`@@ -<start_old>,<len_old> +<start_new>,<len_new> @@\`
  - \`<start_old>\` / \`<start_new>\` are 1-based line numbers.
  - \`<len_old>\` / \`<len_new>\` are the line counts for the hunk in old/new.
  - Multiple hunks per file are allowed; order them top-to-bottom.

### Hunk body line prefixes (strict)
- \`' '\` (space)  = unchanged context line
- \`-\`          = line removed from source
- \`+\`          = line added in target
- Preserve original whitespace and line endings exactly.

### Rules
- The concatenation of all **context + removed** lines in each hunk must appear **verbatim and contiguously** in the source file.
- Keep context minimal but sufficient for unambiguous matching (usually 1-3 lines around changes).
- Multiple files may be patched in one diff, but each requires its own \`---\` / \`+++\` headers and hunks.
- If no changes are needed, output an empty string (no diff).

### Example
--- a/essay.txt
+++ b/essay.txt
@@ -1,4 +1,4 @@
 This is a simple essay.
-It has a bad sentence.
+It has a better sentence.
 The end.`}
          title="Copy diff specification"
        />
      </div>
    ),
    render: HelperFunctionBlocks.ApplyDiffBlock,
    weight: 5,
  },
};

export const BlockIcon = Object.fromEntries(
  Object.entries(blocksDefinition).map(([key, block]) => [key, block.icon]),
) as Record<BlockType, React.ComponentType<React.SVGProps<SVGSVGElement>>>;

const blocks = withCategories(blocksDefinition);
export default blocks;
